import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
    triggerExtendedTranscription,
    fetchExtendedTranscriptionInfo,
} from 'flow/actions/transcription-actions';
import { setSnackbar } from 'flow/actions/app-actions';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import useAppContext from 'flow/hooks/useAppContext';
import { TranscriptionExtended } from 'flow/types/CallRecord';
import { CallRecordKey } from 'flow/types/EntityKeys';
import { selectCallRecordFromKey } from 'flow/selectors/call-selectors';
import ButtonGenerate from './ButtonGenerate';

interface Props {
    callRecordKey: CallRecordKey;
    transcriptionIsPending?: boolean;
    onAwaitTranscriptionConfirmation?: () => void;
    onClickGenerate?: () => void;
}

const ButtonGenerateExtendedTranscription = ({
    callRecordKey,
    transcriptionIsPending,
    onAwaitTranscriptionConfirmation,
    onClickGenerate,
}: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const queryClient = useQueryClient();
    const [userHasRequestedGeneration, setUserHasRequestedGeneration] =
        useState(false);
    const callRecord = useAppSelector((state) =>
        selectCallRecordFromKey(state, callRecordKey),
    );

    const recordedCallKey = callRecord?.getIn(['recordedCall', 'key']);
    if (!recordedCallKey || !callRecord) return null;

    const isPersonal = !!callRecord.getIn([
        'recordedCall',
        'isPersonalRecording',
    ]);
    const extendedTranscriptionKey = callRecord.getIn([
        'extendedTranscriptionInfo',
        'extendedTranscriptionKey',
    ]);

    const handleClick = () => {
        onClickGenerate?.();
        setUserHasRequestedGeneration(true);
        onAwaitTranscriptionConfirmation?.();
        const callRecordQueueKey = callRecord.getIn(['queue', 'key']);
        dispatch(triggerExtendedTranscription(recordedCallKey, isPersonal))
            .then(() => {
                // Fetch extendedTranscriptionInfo (with key and pending: true)
                // to put it in the store, which starts the polling flow.
                void dispatch(
                    fetchExtendedTranscriptionInfo(
                        callRecordKey,
                        callRecordQueueKey,
                    ),
                );
                if (extendedTranscriptionKey) {
                    void queryClient.invalidateQueries({
                        queryKey: [
                            'extendedTranscription',
                            extendedTranscriptionKey,
                        ],
                    });
                }
            })
            .catch((error: { status?: number }) => {
                setUserHasRequestedGeneration(false);
                if (error.status === 402 && extendedTranscriptionKey) {
                    queryClient.setQueryData<TranscriptionExtended>(
                        ['extendedTranscription', extendedTranscriptionKey],
                        (prev) =>
                            prev
                                ? { ...prev, resultCode: 'QUOTA_EXCEEDED' }
                                : ({
                                      resultCode: 'QUOTA_EXCEEDED',
                                  } as TranscriptionExtended),
                    );
                }
                dispatch(
                    setSnackbar({
                        message: i18next.t(
                            error.status === 402
                                ? 'ai.transcript.quota_exceeded'
                                : 'ai.transcript.failed',
                        ),
                    }),
                );
            });
    };

    const transcriptionIsBeingGenerated =
        transcriptionIsPending || userHasRequestedGeneration;

    return (
        <ButtonGenerate
            generationIsInProgress={transcriptionIsBeingGenerated}
            onClick={handleClick}
        />
    );
};

export default ButtonGenerateExtendedTranscription;
